import React, { useState } from 'react'
import { message } from 'antd'
import { useNavigate } from 'react-router-dom'
import { createSubscriptionPlan } from '../../../Web Apis/webApis'
import Load from '../../Load/Load'
import { FaArrowLeft, FaPlus, FaTrash } from 'react-icons/fa'

const AddSubscriptionPage = () => {
    const navigate = useNavigate()
    const [loading, setLoading] = useState(false)

    const [formData, setFormData] = useState({
        code: '',
        displayName: '',
        billingPeriod: 'month',
        durationUnit: 'month',
        durationCount: 1,
        currency: 'INR',
        price: 0,
        originalPrice: null,
        discountPercent: null,
        pricePerMonth: null,
        offerLabel: '',
        badge: { type: 'none', text: '' },
        features: [''],
        ctaText: 'Subscribe Now',
        canCancelAnytime: true,
        isActive: true,
        sortOrder: 0
    })

    const billingPeriods = [
        { value: 'month', label: 'Monthly' },
        { value: 'year', label: 'Yearly' }
    ]

    const durationUnits = [
        { value: 'day', label: 'Day' },
        { value: 'week', label: 'Week' },
        { value: 'month', label: 'Month' },
        { value: 'year', label: 'Year' }
    ]

    const currencies = [
        { value: 'INR', label: 'INR (₹)' },
        { value: 'USD', label: 'USD ($)' },
        { value: 'EUR', label: 'EUR (€)' }
    ]

    const badgeTypes = [
        { value: 'none', label: 'None' },
        { value: 'most_popular', label: 'Most Popular' },
        { value: 'best_value', label: 'Best Value' }
    ]

    const handleInputChange = (e) => {
        const { name, value, type, checked } = e.target
        setFormData(prev => ({
            ...prev,
            [name]: type === 'checkbox' ? checked :
                   type === 'number' ? (value === '' ? 0 : parseFloat(value)) :
                   value
        }))
    }

    const handleBadgeChange = (field, value) => {
        setFormData(prev => ({
            ...prev,
            badge: {
                ...prev.badge,
                [field]: value
            }
        }))
    }

    const addFeature = () => {
        setFormData(prev => ({
            ...prev,
            features: [...prev.features, '']
        }))
    }

    const updateFeature = (index, value) => {
        const updatedFeatures = [...formData.features]
        updatedFeatures[index] = value
        setFormData(prev => ({
            ...prev,
            features: updatedFeatures
        }))
    }

    const removeFeature = (index) => {
        if (formData.features.length > 1) {
            setFormData(prev => ({
                ...prev,
                features: prev.features.filter((_, i) => i !== index)
            }))
        }
    }

    const handleSubmit = async (e) => {
        e.preventDefault()

        // Validate required fields
        if (!formData.code.trim()) {
            message.error("Please enter a plan code")
            return
        }

        if (!formData.displayName.trim()) {
            message.error("Please enter a display name")
            return
        }

        if (formData.price <= 0) {
            message.error("Please enter a valid price")
            return
        }

        // Filter out empty features
        const cleanFeatures = formData.features.filter(feature => feature.trim() !== '')
        if (cleanFeatures.length === 0) {
            message.error("Please add at least one feature")
            return
        }

        try {
            setLoading(true)

            const submitData = {
                ...formData,
                features: cleanFeatures
            }

            const response = await createSubscriptionPlan(submitData)
            if (response?.data?.success) {
                message.success(response?.data?.message || "Subscription plan created successfully")
                navigate('/admin/subscriptions')
            }

        } catch (error) {
            console.log("Error while creating subscription plan", error.message)
            message.error(error?.response?.data?.message || "Error while creating subscription plan")
        } finally {
            setLoading(false)
        }
    }

    const handleGoBack = () => {
        navigate('/admin/subscriptions')
    }

    // Calculate price per month if needed
    const calculatePricePerMonth = () => {
        if (formData.billingPeriod === 'year' && formData.price > 0) {
            return (formData.price / 12).toFixed(2)
        }
        return formData.price
    }

    return (
        <div className="min-h-screen bg-gray-50 p-6">
            <div className="max-w-4xl mx-auto">
                {/* Header */}
                <div className="flex items-center justify-between mb-6">
                    <div className="flex items-center space-x-4">
                        <button
                            onClick={handleGoBack}
                            className="flex items-center px-4 py-2 bg-gray-600 text-white rounded-md hover:bg-gray-700 transition duration-200"
                        >
                            <FaArrowLeft className="mr-2" />
                            Back to Subscriptions
                        </button>
                        <h1 className="text-3xl font-bold text-gray-800">Add New Subscription Plan</h1>
                    </div>
                </div>

                {/* Main Form */}
                <div className="bg-white rounded-lg shadow-md p-6">
                    <form onSubmit={handleSubmit} className="space-y-6">
                        {/* Basic Information */}
                        <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                            <div>
                                <label className="block text-sm font-medium text-gray-700 mb-2">
                                    Plan Code <span className="text-red-500">*</span>
                                </label>
                                <input
                                    type="text"
                                    name="code"
                                    value={formData.code}
                                    onChange={handleInputChange}
                                    className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                                    placeholder="e.g., monthly, premium, yearly"
                                    required
                                />
                            </div>
                            <div>
                                <label className="block text-sm font-medium text-gray-700 mb-2">
                                    Display Name <span className="text-red-500">*</span>
                                </label>
                                <input
                                    type="text"
                                    name="displayName"
                                    value={formData.displayName}
                                    onChange={handleInputChange}
                                    className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                                    placeholder="e.g., Monthly Plan, Premium Plan"
                                    required
                                />
                            </div>
                        </div>

                        {/* Billing Information */}
                        <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
                            <div>
                                <label className="block text-sm font-medium text-gray-700 mb-2">
                                    Billing Period
                                </label>
                                <select
                                    name="billingPeriod"
                                    value={formData.billingPeriod}
                                    onChange={handleInputChange}
                                    className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                                >
                                    {billingPeriods.map(period => (
                                        <option key={period.value} value={period.value}>
                                            {period.label}
                                        </option>
                                    ))}
                                </select>
                            </div>
                            <div>
                                <label className="block text-sm font-medium text-gray-700 mb-2">
                                    Duration
                                </label>
                                <div className="grid grid-cols-2 gap-2">
                                    <input
                                        type="number"
                                        name="durationCount"
                                        value={formData.durationCount}
                                        onChange={handleInputChange}
                                        className="px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                                        placeholder="1"
                                        min="1"
                                    />
                                    <select
                                        name="durationUnit"
                                        value={formData.durationUnit}
                                        onChange={handleInputChange}
                                        className="px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                                    >
                                        {durationUnits.map(unit => (
                                            <option key={unit.value} value={unit.value}>
                                                {unit.label}
                                            </option>
                                        ))}
                                    </select>
                                </div>
                            </div>
                            <div>
                                <label className="block text-sm font-medium text-gray-700 mb-2">
                                    Currency
                                </label>
                                <select
                                    name="currency"
                                    value={formData.currency}
                                    onChange={handleInputChange}
                                    className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                                >
                                    {currencies.map(currency => (
                                        <option key={currency.value} value={currency.value}>
                                            {currency.label}
                                        </option>
                                    ))}
                                </select>
                            </div>
                        </div>

                        {/* Pricing */}
                        <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
                            <div>
                                <label className="block text-sm font-medium text-gray-700 mb-2">
                                    Price <span className="text-red-500">*</span>
                                </label>
                                <input
                                    type="number"
                                    name="price"
                                    value={formData.price}
                                    onChange={handleInputChange}
                                    className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                                    placeholder="0"
                                    min="0"
                                    step="0.01"
                                    required
                                />
                            </div>
                            <div>
                                <label className="block text-sm font-medium text-gray-700 mb-2">
                                    Original Price
                                </label>
                                <input
                                    type="number"
                                    name="originalPrice"
                                    value={formData.originalPrice || ''}
                                    onChange={handleInputChange}
                                    className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                                    placeholder="Optional"
                                    min="0"
                                    step="0.01"
                                />
                            </div>
                            <div>
                                <label className="block text-sm font-medium text-gray-700 mb-2">
                                    Price per Month
                                </label>
                                <input
                                    type="text"
                                    value={calculatePricePerMonth()}
                                    className="w-full px-3 py-2 border border-gray-300 rounded-md bg-gray-50"
                                    readOnly
                                />
                                <p className="text-xs text-gray-500 mt-1">Auto-calculated</p>
                            </div>
                        </div>

                        {/* Badge and CTA */}
                        <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                            <div>
                                <label className="block text-sm font-medium text-gray-700 mb-2">
                                    Badge Type
                                </label>
                                <select
                                    value={formData.badge.type}
                                    onChange={(e) => handleBadgeChange('type', e.target.value)}
                                    className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                                >
                                    {badgeTypes.map(badge => (
                                        <option key={badge.value} value={badge.value}>
                                            {badge.label}
                                        </option>
                                    ))}
                                </select>
                            </div>
                            {formData.badge.type !== 'none' && (
                                <div>
                                    <label className="block text-sm font-medium text-gray-700 mb-2">
                                        Badge Text
                                    </label>
                                    <input
                                        type="text"
                                        value={formData.badge.text}
                                        onChange={(e) => handleBadgeChange('text', e.target.value)}
                                        className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                                        placeholder="e.g., Most Popular"
                                    />
                                </div>
                            )}
                        </div>

                        {/* Features */}
                        <div>
                            <label className="block text-sm font-medium text-gray-700 mb-2">
                                Features <span className="text-red-500">*</span>
                            </label>
                            {formData.features.map((feature, index) => (
                                <div key={index} className="flex gap-2 mb-2">
                                    <input
                                        type="text"
                                        value={feature}
                                        onChange={(e) => updateFeature(index, e.target.value)}
                                        className="flex-1 px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                                        placeholder="Enter feature"
                                    />
                                    {formData.features.length > 1 && (
                                        <button
                                            type="button"
                                            onClick={() => removeFeature(index)}
                                            className="px-3 py-2 bg-red-500 text-white rounded-md hover:bg-red-600"
                                        >
                                            <FaTrash className="text-sm" />
                                        </button>
                                    )}
                                </div>
                            ))}
                            <button
                                type="button"
                                onClick={addFeature}
                                className="flex items-center gap-2 px-4 py-2 bg-green-500 text-white rounded-md hover:bg-green-600"
                            >
                                <FaPlus className="text-sm" />
                                Add Feature
                            </button>
                        </div>

                        {/* CTA and Settings */}
                        <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                            <div>
                                <label className="block text-sm font-medium text-gray-700 mb-2">
                                    Call-to-Action Text
                                </label>
                                <input
                                    type="text"
                                    name="ctaText"
                                    value={formData.ctaText}
                                    onChange={handleInputChange}
                                    className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                                    placeholder="Subscribe Now"
                                />
                            </div>
                            <div>
                                <label className="block text-sm font-medium text-gray-700 mb-2">
                                    Sort Order
                                </label>
                                <input
                                    type="number"
                                    name="sortOrder"
                                    value={formData.sortOrder}
                                    onChange={handleInputChange}
                                    className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                                    placeholder="0"
                                    min="0"
                                />
                            </div>
                        </div>

                        {/* Settings */}
                        <div className="flex gap-6">
                            <label className="flex items-center">
                                <input
                                    type="checkbox"
                                    name="isActive"
                                    checked={formData.isActive}
                                    onChange={handleInputChange}
                                    className="mr-2"
                                />
                                <span className="text-sm font-medium text-gray-700">Active Plan</span>
                            </label>
                            <label className="flex items-center">
                                <input
                                    type="checkbox"
                                    name="canCancelAnytime"
                                    checked={formData.canCancelAnytime}
                                    onChange={handleInputChange}
                                    className="mr-2"
                                />
                                <span className="text-sm font-medium text-gray-700">Can Cancel Anytime</span>
                            </label>
                        </div>

                        {/* Submit Buttons */}
                        <div className="flex justify-end space-x-4 pt-6 border-t">
                            <button
                                type="button"
                                onClick={handleGoBack}
                                className="px-6 py-2 bg-gray-500 text-white rounded-md hover:bg-gray-600 transition duration-200"
                            >
                                Cancel
                            </button>
                            <button
                                type="submit"
                                disabled={loading}
                                className="px-6 py-2 bg-blue-600 text-white rounded-md hover:bg-blue-700 transition duration-200 disabled:bg-gray-400 disabled:cursor-not-allowed"
                            >
                                {loading ? "Creating..." : "Create Plan"}
                            </button>
                        </div>
                    </form>
                </div>
            </div>

            {loading && <Load />}
        </div>
    )
}

export default AddSubscriptionPage

